/**
 * theme.js - global UI utilities for the "not your fault!" theme.
 *
 * Vanilla replacements for the reference project's framer-motion pieces:
 *   - reveal-on-scroll (IntersectionObserver -> .reveal.is-in)
 *   - scroll parallax ([data-parallax], [data-parallax-scale])
 *   - 3D pointer tilt (<tilt-card>)
 *   - toast notifications (window.themeToast)
 *   - mobile menu + notify-me buttons (event delegation)
 *
 * Everything is inert when animations are disabled in theme settings
 * (html.anims missing) or the visitor prefers reduced motion.
 */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const animsEnabled = () =>
    document.documentElement.classList.contains('anims') && !reduceMotion.matches;

  /* ---------- OFF20 / coupon prices on cards + cart ---------- */
  const COUPON_KEY = 'nyf:pdp-coupon';
  const COUPON_PCT_KEY = 'nyf:pdp-coupon-percent';

  function formatCouponMoney(cents) {
    const value = Number(cents) / 100;
    if (Number.isNaN(value)) return '';
    const amount = value.toFixed(2);
    const parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const pretty = parts.join('.');
    const format = document.documentElement.getAttribute('data-money-format') || '';
    if (!format) return 'Rs. ' + pretty;
    return format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(value)))
      .replace(/\{\{\s*amount\s*\}\}/g, pretty);
  }

  function getAppliedCoupon() {
    try {
      const code = (sessionStorage.getItem(COUPON_KEY) || '').trim();
      const percent = Number(sessionStorage.getItem(COUPON_PCT_KEY) || 0);
      if (!code) return null;
      return { code, percent: percent > 0 ? percent : 20 };
    } catch (err) {
      return null;
    }
  }

  function discountedCents(cents, percent) {
    return Math.round((Number(cents) * (100 - percent)) / 100);
  }

  function shopifyAlreadyDiscounted(original, final) {
    return Number(final) > 0 && Number(final) < Number(original) * 0.995;
  }

  function paintPriceEl(el, percent) {
    const list = Number(el.dataset.priceCents || 0);
    if (!list) return;
    const compare = Number(el.dataset.compareCents || 0);
    const nowCents = discountedCents(list, percent);
    const strikeCents = Math.max(compare, list);
    let strike = el.querySelector('s');
    const nowEl = el.querySelector('.price-now, b');
    if (strikeCents > nowCents) {
      if (!strike) {
        strike = document.createElement('s');
        el.insertBefore(strike, nowEl || el.firstChild);
      }
      strike.hidden = false;
      strike.textContent = formatCouponMoney(strikeCents);
    }
    if (nowEl) nowEl.textContent = formatCouponMoney(nowCents);
  }

  function paintCartEl(el, percent) {
    const original = Number(el.dataset.originalCents || 0);
    const final = Number(el.dataset.finalCents || original);
    if (!original) return;
    const nowCents = shopifyAlreadyDiscounted(original, final)
      ? final
      : discountedCents(original, percent);
    if (nowCents >= original) {
      el.textContent = formatCouponMoney(final || original);
      return;
    }
    el.innerHTML = '<s>' + formatCouponMoney(original) + '</s> ' + formatCouponMoney(nowCents);
  }

  function paintCouponPrices(scope) {
    const offer = getAppliedCoupon();
    if (!offer) return;
    const root = scope || document;
    root.querySelectorAll('[data-price-cents]').forEach((el) => {
      if (el.tagName === 'INPUT' || el.tagName === 'OPTION') return;
      paintPriceEl(el, offer.percent);
    });
    root.querySelectorAll('[data-cart-line], [data-cart-total]').forEach((el) => {
      paintCartEl(el, offer.percent);
    });
    ensureCartCoupon(root, offer);
  }

  function ensureCartCoupon(root, offer) {
    root.querySelectorAll('.drawer-foot, .cart-page__foot').forEach((foot) => {
      if (foot.querySelector('[data-cart-coupon]')) return;
      const host = foot.closest('[data-coupon-applied-label]');
      const template = (host && host.dataset.couponAppliedLabel) || '__CODE__ applied';
      const row = document.createElement('div');
      row.className = 'row cart-coupon';
      row.setAttribute('data-cart-coupon', 'session');
      const chip = document.createElement('span');
      chip.className = 'cart-coupon-chip';
      chip.appendChild(document.createTextNode(template.replace('__CODE__', String(offer.code || '').toUpperCase())));
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'pdp-coupon-remove';
      remove.setAttribute('data-coupon-remove', '');
      remove.setAttribute('aria-label', 'Remove coupon');
      remove.textContent = '\u00d7';
      chip.appendChild(remove);
      row.appendChild(chip);
      foot.insertBefore(row, foot.firstChild);
    });
  }

  function restoreCouponPrices(scope) {
    const root = scope || document;
    root.querySelectorAll('[data-price-cents]').forEach((el) => {
      if (el.tagName === 'INPUT' || el.tagName === 'OPTION') return;
      const list = Number(el.dataset.priceCents || 0);
      const compare = Number(el.dataset.compareCents || 0);
      const nowEl = el.querySelector('.price-now, b');
      let strike = el.querySelector('s');
      if (compare > list) {
        if (!strike) {
          strike = document.createElement('s');
          el.insertBefore(strike, nowEl || el.firstChild);
        }
        strike.hidden = false;
        strike.textContent = formatCouponMoney(compare);
      } else if (strike) {
        strike.remove();
      }
      if (nowEl && list) nowEl.textContent = formatCouponMoney(list);
    });
    root.querySelectorAll('[data-cart-line], [data-cart-total]').forEach((el) => {
      const final = Number(el.dataset.finalCents || el.dataset.originalCents || 0);
      if (final) el.textContent = formatCouponMoney(final);
    });
    root.querySelectorAll('[data-cart-coupon="session"]').forEach((el) => el.remove());
  }

  window.nyfCoupon = {
    get: getAppliedCoupon,
    paint: paintCouponPrices,
    set(code, percent) {
      try {
        sessionStorage.setItem(COUPON_KEY, code);
        sessionStorage.setItem(COUPON_PCT_KEY, String(percent || 20));
      } catch (err) {
        /* ignore */
      }
      paintCouponPrices(document);
    },
    clear() {
      try {
        sessionStorage.removeItem(COUPON_KEY);
        sessionStorage.removeItem(COUPON_PCT_KEY);
        sessionStorage.removeItem('nyf:coupon-attempt');
      } catch (err) {
        /* ignore */
      }
      restoreCouponPrices(document);
    },
  };

  document.addEventListener('nyf:coupon-applied', () => paintCouponPrices(document));
  document.addEventListener('nyf:coupon-removed', () => restoreCouponPrices(document));
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-coupon-remove]');
    if (!button) return;
    event.preventDefault();
    button.disabled = true;
    if (window.nyfCoupon && typeof window.nyfCoupon.clear === 'function') window.nyfCoupon.clear();
    const host = button.closest('[data-root]') || document.querySelector('[data-root]');
    const root = (host && host.dataset.root) || '/';
    try {
      await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: '' }),
      });
    } catch (err) {
      /* reload still clears the saved code on this page */
    }
    window.location.reload();
  });
  document.addEventListener('DOMContentLoaded', () => paintCouponPrices(document));
  paintCouponPrices(document);

  /* ---------- toast ---------- */
  let toastTimer = null;
  window.themeToast = (message) => {
    const toast = document.querySelector('[data-toast]');
    if (!toast) return;
    toast.querySelector('[data-toast-message]').textContent = message;
    toast.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('show'), 2200);
  };

  /* ---------- reveal on scroll ---------- */
  let revealObserver = null;

  function initReveals(scope) {
    const targets = scope.querySelectorAll('.reveal:not(.is-in)');
    if (!animsEnabled()) {
      targets.forEach((el) => el.classList.add('is-in'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-in');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -80px 0px' }
      );
    }
    targets.forEach((el) => revealObserver.observe(el));
  }

  /* ---------- scroll parallax ----------
     [data-parallax="0.14"]  -> translateY(progress * 0.14 * host height)
     [data-parallax-scale="1.1,0.08"] -> scale(1.1 + 0.08 * |progress|)
     progress is +1 when the host enters at the bottom of the viewport,
     0 when centered, -1 when it leaves at the top (mirrors the reference's
     useScroll("start end" -> "end start") ranges). */
  const parallaxEls = new Set();
  let parallaxTicking = false;

  function scanParallax(scope) {
    scope.querySelectorAll('[data-parallax]').forEach((el) => parallaxEls.add(el));
    updateParallax();
  }

  function updateParallax() {
    parallaxTicking = false;
    if (!animsEnabled() || parallaxEls.size === 0) return;
    const vh = window.innerHeight;
    parallaxEls.forEach((el) => {
      if (!el.isConnected) {
        parallaxEls.delete(el);
        return;
      }
      const host = el.closest('[data-parallax-host]') || el.parentElement;
      const rect = host.getBoundingClientRect();
      if (rect.bottom < -vh || rect.top > vh * 2) return;
      const raw = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2);
      const p = Math.max(-1, Math.min(1, raw));
      const speed = parseFloat(el.dataset.parallax) || 0;
      let transform = `translate3d(0, ${(p * speed * rect.height).toFixed(2)}px, 0)`;
      if (el.dataset.parallaxScale) {
        const [base, delta] = el.dataset.parallaxScale.split(',').map(parseFloat);
        transform += ` scale(${(base + delta * Math.abs(p)).toFixed(4)})`;
      }
      el.style.transform = transform;
    });
  }

  function requestParallax() {
    if (!parallaxTicking) {
      parallaxTicking = true;
      requestAnimationFrame(updateParallax);
    }
  }

  window.addEventListener('scroll', requestParallax, { passive: true });
  window.addEventListener('resize', requestParallax);

  /* ---------- 3D pointer tilt ---------- */
  class TiltCard extends HTMLElement {
    connectedCallback() {
      this.inner = this.querySelector('.tilt-inner');
      if (!this.inner || !animsEnabled()) return;
      this.max = parseFloat(this.getAttribute('max')) || 13;
      this.current = { x: 0, y: 0, g: 50 };
      this.target = { x: 0, y: 0, g: 50 };
      this.raf = null;
      this.addEventListener('pointermove', (e) => this.onMove(e));
      this.addEventListener('pointerleave', () => this.onLeave());
    }

    onMove(e) {
      const rect = this.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width - 0.5;
      const py = (e.clientY - rect.top) / rect.height - 0.5;
      this.target = {
        x: py * -2 * this.max,
        y: px * 2 * this.max,
        g: 50 + px * 60,
      };
      this.animate_();
    }

    onLeave() {
      this.target = { x: 0, y: 0, g: 50 };
      this.animate_();
    }

    animate_() {
      if (this.raf) return;
      const step = () => {
        const c = this.current;
        const t = this.target;
        c.x += (t.x - c.x) * 0.12;
        c.y += (t.y - c.y) * 0.12;
        c.g += (t.g - c.g) * 0.12;
        this.inner.style.transform = `rotateX(${c.x.toFixed(2)}deg) rotateY(${c.y.toFixed(2)}deg)`;
        this.inner.style.setProperty('--gx', `${c.g.toFixed(1)}%`);
        const settled =
          Math.abs(t.x - c.x) < 0.02 && Math.abs(t.y - c.y) < 0.02 && Math.abs(t.g - c.g) < 0.1;
        this.raf = settled ? null : requestAnimationFrame(step);
      };
      this.raf = requestAnimationFrame(step);
    }
  }
  if (!customElements.get('tilt-card')) customElements.define('tilt-card', TiltCard);

  /* ---------- delegated UI events ---------- */
  document.addEventListener('click', (e) => {
    // mobile menu toggle
    const menuToggle = e.target.closest('[data-menu-toggle]');
    if (menuToggle) {
      const header = menuToggle.closest('header');
      const menu = header && header.querySelector('.mobile-menu');
      if (menu) {
        const open = menu.classList.toggle('open');
        menuToggle.setAttribute('aria-expanded', String(open));
        menuToggle.querySelector('.burger__open').hidden = open;
        menuToggle.querySelector('.burger__close').hidden = !open;
      }
      return;
    }

    // close the mobile menu when a link inside it is chosen
    if (e.target.closest('.mobile-menu a')) {
      const menu = e.target.closest('.mobile-menu');
      menu.classList.remove('open');
      const toggle = menu.closest('header').querySelector('[data-menu-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.querySelector('.burger__open').hidden = false;
        toggle.querySelector('.burger__close').hidden = true;
      }
      return;
    }

    // "notify me" style buttons: show their message as a toast
    const notify = e.target.closest('[data-notify]');
    if (notify) {
      window.themeToast(notify.dataset.notify);
    }
  });

  /* ---------- init ---------- */
  function init(scope) {
    initReveals(scope);
    scanParallax(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  // Re-init inside the theme editor when sections are (re)loaded
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
